/**
 * v2 shared UI primitives. Small on purpose - enough that every screen
 * composes the same card/section/skeleton/ticker language without a
 * component-library dependency.
 */
import { useEffect, useRef, useState } from "react";

/** Standard elevated card: hairline border + soft shadow. */
export function Card({
  className = "",
  children,
  onClick,
}: {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={`card ${onClick ? "pressable text-left w-full" : ""} ${className}`}
    >
      {children}
    </Tag>
  );
}

/** Tiny uppercase section label above a card group. */
export function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-mute px-1">
      {children}
    </h2>
  );
}

/** Shimmering placeholder block sized by the caller. */
export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`skeleton ${className}`} aria-hidden />;
}

/** Colored status/count pill. */
export function Pill({
  tone = "mute",
  children,
}: {
  tone?: "accent" | "sun" | "home" | "grid" | "danger" | "mute";
  children: React.ReactNode;
}) {
  const tones: Record<string, string> = {
    accent: "bg-accent-soft text-accent",
    sun: "bg-sun-soft text-sun",
    home: "bg-home-soft text-home",
    grid: "bg-grid-soft text-grid",
    danger: "bg-danger-soft text-danger",
    mute: "bg-card-soft text-ink-mute",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * Number ticker - tweens between values on change (280 ms ease-out).
 * Renders via a formatter so the same component drives W, kWh, €, %.
 * Respects prefers-reduced-motion (jumps instantly).
 */
export function AnimatedNumber({
  value,
  format,
  className = "",
}: {
  value: number;
  format: (v: number) => string;
  className?: string;
}) {
  const [shown, setShown] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef(0);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !Number.isFinite(shown)) {
      fromRef.current = value;
      setShown(value);
      return;
    }
    const from = fromRef.current;
    if (from === value) return;
    const t0 = performance.now();
    const DUR = 280;
    cancelAnimationFrame(rafRef.current);
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / DUR);
      const eased = 1 - (1 - p) * (1 - p);
      const v = from + (value - from) * eased;
      setShown(p >= 1 ? value : v);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = value;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return <span className={`tabular ${className}`}>{format(shown)}</span>;
}

/**
 * Screen header shared by every tab: big title, optional subtitle,
 * optional right-side actions. Keeps the five screens visually in step.
 */
export function ScreenHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <header className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        {subtitle && (
          <div className="text-sm text-ink-mute truncate">{subtitle}</div>
        )}
        <h1 className="text-[26px] font-bold tracking-tight leading-tight">
          {title}
        </h1>
      </div>
      {right && <div className="flex gap-2 shrink-0 pb-0.5">{right}</div>}
    </header>
  );
}

/** Round icon button used in headers. */
export function IconButton({
  ariaLabel,
  onClick,
  disabled,
  children,
}: {
  ariaLabel: string;
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className="pressable w-10 h-10 rounded-full bg-card border border-card-border shadow-card grid place-items-center text-ink hover:text-accent disabled:opacity-40 disabled:hover:text-ink"
    >
      {children}
    </button>
  );
}
